import type { ReportQueryRequest } from '@/api/types';
import type {
  PerformanceReportSearch,
  PerformanceReportTab,
} from '@/lib/performance-report-search';

/** The canonical grouping behind each visible Performance tab. */
export const PERFORMANCE_TAB_GROUPS: Record<
  PerformanceReportTab,
  ReportQueryRequest['group_by']
> = {
  overview: ['assigned_production'],
  campaign: ['campaign', 'assigned_production', 'channel'],
  production: ['assigned_production'],
  channel: ['channel'],
  audience: ['audience'],
  audienceType: ['audience_type'],
  creative: ['creative', 'assigned_production'],
};

/**
 * Translate validated URL state into the only filter shape the show-facing
 * report may issue. Internal attribution-effect, sold-production, placement,
 * and provider-object filters are deliberately unreachable here.
 */
export function buildPerformanceReportFilters(
  search: PerformanceReportSearch,
): ReportQueryRequest['filters'] {
  return {
    assigned_production_ids: search.productionId ? [search.productionId] : [],
    sold_production_ids: [],
    campaign_ids: search.campaignId ? [search.campaignId] : [],
    audience_ids: search.audienceId ? [search.audienceId] : [],
    audience_revision: search.audienceRevision ?? null,
    creative_ids: search.creativeId ? [search.creativeId] : [],
    channels: search.channel ? [search.channel] : [],
    audience_types: search.audienceType ? [search.audienceType] : [],
    targets: search.target ? [search.target] : [],
    placements: [],
    currencies: ['USD'],
    provider_ad_ids: [],
    provider_creative_ids: [],
    attribution_effects: [],
    attribution_settings: [],
  };
}

/**
 * Pure URL-state-to-wire builder shared verbatim by the route loader, screen,
 * pagination, and export. Dataset and attribution scope are invariants rather
 * than caller-controlled arguments, so stale URLs cannot weaken them.
 */
export function buildPerformanceReportRequest(
  search: PerformanceReportSearch,
  groupBy: ReportQueryRequest['group_by'],
  filters: ReportQueryRequest['filters'],
  limit: number | null = 500,
  ordered = true,
): ReportQueryRequest {
  return {
    dataset: 'performance',
    attribution_scope: 'direct_only',
    date_from: search.from,
    date_to: search.to,
    group_by: groupBy,
    filters,
    order: ordered
      ? { field: search.sortField, direction: search.sortDirection }
      : null,
    cursor: null,
    limit,
  };
}
